import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';

/**
 * Button variants — Quiet Atlas (Direction A) tokens.
 *
 * Single terra accent for primary, ink for secondary/ghost, semantic
 * moss/rust for success/danger. Cream paper background, warm shadows.
 * Order: base → variant → size → state.
 *
 * Exported for composition (e.g., styling react-router Link as button).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-md',
    'font-sans font-medium',
    'transition-colors whitespace-nowrap',
    'focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-2 focus:ring-offset-paper',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-terra-600 text-paper hover:bg-terra-700 shadow-sm',
        secondary:
          'border border-ink-100 bg-paper text-ink-700 hover:bg-paper-2 hover:border-ink-300 shadow-sm',
        ghost: 'text-ink-500 hover:text-ink-900 hover:bg-paper-2',
        success: 'bg-moss-600 text-paper hover:opacity-90 shadow-sm',
        danger: 'bg-rust-600 text-paper hover:opacity-90 shadow-sm',
      },
      size: {
        sm: 'text-sm py-1.5 px-3',
        md: 'text-sm py-2 px-4',
        lg: 'text-base py-3 px-6',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      disabled,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? (
          <Loader2
            className="h-4 w-4 animate-spin"
            aria-hidden="true"
            data-testid="button-spinner"
          />
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';
